import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations } from "../i18n/translations";

// AstroFate i18n context.
//
// Behaviour:
//   • English is the default
//   • Hindi is opt-in; selection persists in localStorage("astrofate_lang")
//   • Missing keys fall back to English, then to the key itself (so missing
//     translations are obvious during development without crashing the UI)
//   • Hindi values may freely mix Hinglish — see translations.js comment

const STORAGE_KEY = "astrofate_lang";
const SUPPORTED = ["en", "hi"];

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
      return SUPPORTED.includes(stored) ? stored : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, lang); } catch { /* private mode etc. */ }
    // Update <html lang="…"> so screen readers and search engines pick it up.
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const t = useCallback((key, vars) => {
    const dict = translations[lang] || translations.en;
    let val = dict[key];
    if (val === undefined) val = translations.en[key];
    if (val === undefined) return key;
    if (vars && typeof val === "string") {
      Object.entries(vars).forEach(([k, v]) => {
        val = val.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
      });
    }
    return val;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
